import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  CheckCircle2, Clock, Search, Award, Users, Loader2, X, Save, ShieldCheck, AlertCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminApprovazioniRegistro() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [tab, setTab] = useState("eleggibili"); // eleggibili | iscritti | tutti
  const [approving, setApproving] = useState(null); // user object in modal
  const [form, setForm] = useState({ citta: "", provincia: "", specializzazione: "Consulente Digitale" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(null); // user_id in azione

  const handleAction = async (u, action) => {
    setBusy(u.user_id);
    setError("");
    try {
      const url = `${API}/api/admin/users/${u.user_id}/${action}`;
      const body = action === "add-cfdt" ? { amount: 5 } : action === "complete-course" ? { cfdt_reward: 20 } : {};
      await axios.post(url, body, { withCredentials: true });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Errore azione");
    } finally {
      setBusy(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/registro/eleggibili`, { withCredentials: true });
      setUsers(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Errore caricamento utenti");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = users.filter((u) => {
    if (tab === "eleggibili" && (!u.eleggibile || u.gia_iscritto)) return false;
    if (tab === "iscritti" && !u.gia_iscritto) return false;
    const q = filter.toLowerCase();
    return !q || [u.nome, u.cognome, u.email].some((v) => (v || "").toLowerCase().includes(q));
  });

  const counts = {
    eleggibili: users.filter(u => u.eleggibile && !u.gia_iscritto).length,
    iscritti: users.filter(u => u.gia_iscritto).length,
    tutti: users.length,
  };

  const handleApprove = async () => {
    if (!approving) return;
    setSaving(true);
    setError("");
    try {
      await axios.post(`${API}/api/registro/approva`, {
        user_id: approving.user_id,
        citta: form.citta || null,
        provincia: form.provincia || null,
        specializzazione: form.specializzazione || null,
      }, { withCredentials: true });
      setApproving(null);
      setForm({ citta: "", provincia: "", specializzazione: "Consulente Digitale" });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Errore approvazione");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div>
        <h2 className="text-2xl font-extrabold">Approvazione Iscrizioni Registro</h2>
        <p className="text-[13.5px] text-slate-400 mt-1">Gestisci le iscrizioni al Registro Nazionale dei soci A.C.D.T. (corso base completato o 20+ CFDT).</p>
        <p className="text-[11.5px] text-amber-300 mt-2">
          🧪 <strong>Test/Dev:</strong> usa <span className="text-emerald-300">&quot;✓ Corso&quot;</span> per marcare il corso base + 20 CFDT, oppure <span className="text-amber-300">&quot;+5 CFDT&quot;</span> per aggiungere progressivamente fino a 20.
        </p>
      </div>

      {/* KPI */}
      <div className="grid sm:grid-cols-3 gap-4">
        <KPI label="Eleggibili da approvare" value={counts.eleggibili} icon={Clock} color="#f59e0b" />
        <KPI label="Già iscritti" value={counts.iscritti} icon={CheckCircle2} color="#22c55e" />
        <KPI label="Utenti totali" value={counts.tutti} icon={Users} color="#3b82f6" />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 bg-white/5 border border-white/10 p-1 rounded-lg w-fit">
        {[
          { id: "eleggibili", label: `Eleggibili (${counts.eleggibili})` },
          { id: "iscritti", label: `Iscritti (${counts.iscritti})` },
          { id: "tutti", label: `Tutti (${counts.tutti})` },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-md text-[12.5px] font-extrabold transition-colors ${tab === t.id ? "bg-[#f59e0b] text-[#0a1020]" : "text-slate-300 hover:bg-white/5"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Cerca per nome o email..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="pl-10 h-11 bg-white/5 border-white/10 text-white placeholder:text-slate-500"
        />
      </div>

      {/* Tabella */}
      <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#f59e0b]" /></div>
        ) : (
          <table className="w-full text-[13.5px]">
            <thead className="bg-white/5">
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400">
                <th className="p-4">Socio</th>
                <th className="p-4">CFDT</th>
                <th className="p-4">Corso Base</th>
                <th className="p-4">Stato Eleggibilità</th>
                <th className="p-4 text-right">Azione</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.user_id} data-testid={`elig-row-${u.user_id}`} className="border-t border-white/5 hover:bg-white/5">
                  <td className="p-4">
                    <div className="font-bold">{u.nome} {u.cognome}</div>
                    <div className="text-[11.5px] text-slate-400 mt-0.5">{u.email}</div>
                  </td>
                  <td className="p-4 font-extrabold">
                    <span className={u.cfdt_anno >= 20 ? "text-emerald-400" : "text-slate-300"}>{u.cfdt_anno}</span>
                    <span className="text-slate-500 font-normal text-[11px]"> / 20</span>
                  </td>
                  <td className="p-4">
                    {u.corso_completato ? (
                      <span className="inline-flex items-center gap-1 text-emerald-400 text-[12px] font-bold">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Completato
                      </span>
                    ) : (
                      <span className="text-slate-500 text-[12px]">—</span>
                    )}
                  </td>
                  <td className="p-4">
                    {u.gia_iscritto ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-300 text-[11px] font-extrabold uppercase tracking-wider">
                        <ShieldCheck className="h-3 w-3" /> Iscritto Registro
                      </span>
                    ) : u.eleggibile ? (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-300 text-[11px] font-extrabold uppercase tracking-wider">
                        <Clock className="h-3 w-3" /> Pronto per approvazione
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-500/15 text-slate-400 text-[11px] font-extrabold uppercase tracking-wider">
                        Non eleggibile
                      </span>
                    )}
                    <div className="text-[10.5px] text-slate-500 mt-1">{u.motivo}</div>
                  </td>
                  <td className="p-4 text-right">
                    {u.gia_iscritto ? (
                      <div className="flex items-center gap-1 justify-end">
                        <span className="text-[11px] text-emerald-400 font-bold">✓ Approvato</span>
                        <button
                          onClick={() => handleAction(u, "reset")}
                          className="ml-2 h-8 px-2 text-[10.5px] font-bold text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded"
                          title="Reset utente (dev)"
                        >Reset</button>
                      </div>
                    ) : u.eleggibile ? (
                      <Button
                        data-testid={`btn-approva-${u.user_id}`}
                        onClick={() => setApproving(u)}
                        className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1020] font-extrabold h-9"
                      >
                        <Award className="mr-2 h-4 w-4" /> Approva
                      </Button>
                    ) : (
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          data-testid={`btn-test-corso-${u.user_id}`}
                          onClick={() => handleAction(u, "complete-course")}
                          disabled={busy === u.user_id}
                          className="h-8 px-2.5 text-[11px] font-bold bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 rounded disabled:opacity-50"
                          title="Completa corso base (+20 CFDT)"
                        >✓ Corso</button>
                        <button
                          data-testid={`btn-test-cfdt-${u.user_id}`}
                          onClick={() => handleAction(u, "add-cfdt")}
                          disabled={busy === u.user_id}
                          className="h-8 px-2.5 text-[11px] font-bold bg-amber-500/15 text-amber-300 hover:bg-amber-500/25 rounded disabled:opacity-50"
                          title="Aggiungi +5 CFDT"
                        >+5 CFDT</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="p-8 text-center text-slate-400">Nessun utente in questa categoria</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {error && !approving && (
        <div className="rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 p-3 text-[13px]">{error}</div>
      )}

      {/* Modal Approva */}
      {approving && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#0a1020] border border-white/10 rounded-2xl p-6 w-full max-w-lg text-white">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-extrabold">Approva iscrizione al Registro</h3>
              <button onClick={() => setApproving(null)} className="h-9 w-9 rounded-lg hover:bg-white/10 flex items-center justify-center">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="rounded-xl bg-white/5 border border-white/10 p-4 mb-5">
              <div className="font-bold text-[15px]">{approving.nome} {approving.cognome}</div>
              <div className="text-[12px] text-slate-400 mt-0.5">{approving.email}</div>
              <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 text-[10.5px] font-extrabold uppercase tracking-wider">
                <CheckCircle2 className="h-3 w-3" /> {approving.motivo}
              </div>
            </div>

            <div className="mb-5 rounded-lg bg-[#f59e0b]/10 border border-[#f59e0b]/30 p-3 text-[12px] text-amber-200 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>I dati pubblici qui inseriti compariranno nell&apos;elenco del Registro Nazionale.</span>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <Label className="text-[11.5px] uppercase tracking-wider text-slate-400 font-bold">Città</Label>
                  <Input value={form.citta} onChange={(e) => setForm({ ...form, citta: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 text-white" placeholder="Milano" />
                </div>
                <div>
                  <Label className="text-[11.5px] uppercase tracking-wider text-slate-400 font-bold">Prov.</Label>
                  <Input value={form.provincia} onChange={(e) => setForm({ ...form, provincia: e.target.value.toUpperCase().slice(0, 2) })} className="mt-1.5 bg-white/5 border-white/10 text-white uppercase" maxLength={2} placeholder="MI" />
                </div>
              </div>
              <div>
                <Label className="text-[11.5px] uppercase tracking-wider text-slate-400 font-bold">Specializzazione</Label>
                <Input value={form.specializzazione} onChange={(e) => setForm({ ...form, specializzazione: e.target.value })} className="mt-1.5 bg-white/5 border-white/10 text-white" />
              </div>
            </div>

            {error && (
              <div className="mt-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 p-3 text-[13px]">{error}</div>
            )}

            <div className="mt-6 flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setApproving(null)} className="border-white/10 bg-white/5 hover:bg-white/10 text-white">
                <X className="mr-2 h-4 w-4" /> Annulla
              </Button>
              <Button data-testid="btn-conferma-approva" onClick={handleApprove} disabled={saving} className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1020] font-extrabold">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" /> Approva ora</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function KPI({ label, value, icon: Icon, color }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-5 flex items-center gap-4">
      <div className="h-12 w-12 rounded-xl flex items-center justify-center" style={{ background: `${color}20`, color }}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-bold">{label}</div>
        <div className="text-3xl font-extrabold">{value}</div>
      </div>
    </div>
  );
}
